import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { InfiniteQueryResult, QueryKey } from "react-query";

import { useTranslation } from "react-i18next";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GridApi, RowNode } from "@ag-grid-community/core";

import { DataGrid, ExportPictusJson, showNotification } from "fe-shared/src/components";
import ExportExcelData from "fe-shared/src/components/ExportExcelData";
import { CloseIcon } from "fe-shared/src/components/Icons";
import {
  getFlattenedTransactions,
  getRequestParams,
} from "fe-shared-transactions/src/helpers/transactionsRequestConverter";
import useTransactionsExport from "fe-shared-transactions/src/hooks/useTransactionsExport";
import usePictusJsonExport from "fe-shared-transactions/src/hooks/usePictusJsonExport";
import { getTransactionRowHeight } from "fe-shared-transactions/src/helpers/transactions";

import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";
import BusinessRole from "fe-shared/src/types/domain/BusinessRole";
import {
  GridTransactionList,
  TransactionType,
  TransactionStatus,
} from "fe-shared-transactions/src/types";
import { Company } from "fe-shared/src/types/domain";
import { CreateInvoiceButton } from "fe-shared-transactions/src/components";
import useTenantOptions from "fe-shared/src/hooks/useTenantOptions";
import UserContext from "../../contexts/userContext";
import { GridTransactionExternal } from "../../types/transaction";
import { renderers, getColumnDefs } from "./modules/config";
import { traceBatchHistory } from "./modules/api";

import { getTransactions } from "../../routes/TransactionsList/modules/api";

import styles from "./TransactionsListGrid.module.scss";

interface Props extends AgGridReactProps {
  transactions: InfiniteQueryResult<
    GridTransactionList<GridTransactionExternal>
  >;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
  lastOpenedNumber: string | null;
  clearLastOpenedNumber: () => void;
  businessRoleCode: BusinessRole;
  canCreateInvoice: boolean;
  canExportTransactionsList: boolean;
  canExportPictusJson: boolean;
  company: Company;
  transactionsQueryKey: QueryKey;
}

const TransactionsListGrid = (props: Props) => {
  const {
    transactions,
    lastOpenedNumber,
    clearLastOpenedNumber,
    businessRoleCode,
    canCreateInvoice,
    canExportTransactionsList,
    canExportPictusJson,
    transactionsQueryKey,
    company: { companyId, displayName },
    filters,
    ...otherProps
  } = props;
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [tenantOptions] = useTenantOptions();
  const gridApiRef = useRef<GridApi | null>(null);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedRows, setSelectedRows] = useState<GridTransactionExternal[]>(
    []
  );
  const requestParams = getRequestParams(filters, otherProps.options);
  const { columnConfig, getDataRows } = useTransactionsExport(
    requestParams,
    companyId
  );
  const { getExportParams } = usePictusJsonExport(requestParams);

  const columnDefs = useMemo(
    () => getColumnDefs(t, businessRoleCode, canCreateInvoice),
    [t, businessRoleCode, canCreateInvoice]
  );

  const assignGridApi = (api: GridApi) => {
    gridApiRef.current = api;
  };

  const onFirstDataRendered = useCallback(() => {
    if (lastOpenedNumber) {
      gridApiRef.current!.ensureNodeVisible(
        (node: RowNode) => node.id === lastOpenedNumber.toString(),
        "middle"
      );

      clearLastOpenedNumber();
    }
  }, [lastOpenedNumber, clearLastOpenedNumber]);

  const rowCount = transactions.data?.[0].total || 0;

  const rowData = useMemo(() => getFlattenedTransactions(transactions.data), [
    transactions.data,
  ]);

  const getTransactionNumbers = async () => {
    const selectedTransactions =
      isAllSelected && selectedRows.length < rowCount
        ? await getTransactions("", requestParams, companyId, null).then(
            (res) => res.transactions
          )
        : selectedRows;
    const deliveryToProcessorTransactions = selectedTransactions.filter(
      (tr) =>
        tr.transactionType === TransactionType.Delivery ||
        (tr.transactionType === TransactionType.Adjustment &&
          tr.linkedTransactions.find(
            (lt) => lt.transactionType === TransactionType.Delivery
          ))
    );

    if (deliveryToProcessorTransactions.length) {
      await traceBatchHistory(
        deliveryToProcessorTransactions.map((tr) => tr.transactionNumber)
      );
    }

    return selectedTransactions.map((tr) => tr.transactionNumber);
  };

  const isCreateInvoiceEnabled =
    selectedRows.length > 0 && selectedRows.every((row) => row.isInvoiceable);

  const isShowInvoiceableApplied = filters?.isInvoiceable?.value === "true";

  // Check if Pictus JSON export should be available
  // Only for Receipt transaction type filter and Approved status
  const isReceiptOrDeliveryType = 
    Array.isArray(filters.transactionType?.value) &&
    filters.transactionType.value.length === 1 &&
    (filters.transactionType.value[0] === TransactionType.Receipt || 
     filters.transactionType.value[0] === TransactionType.Delivery);

  const isPictusJsonExportable = 
    canExportPictusJson && 
    tenantOptions.webOptionFlags?.ExportPictusJSON === true &&
    isReceiptOrDeliveryType &&
    filters.statusCode?.value === TransactionStatus.Approved;
  
  const onExportError = (error: any) => {
    showNotification(
      error.response?.data.message || error.message,
      undefined,
      CloseIcon
    );
  };

  const setIsShowInvoiceable = () => {
    gridApiRef.current!.setFilterModel({
      ...filters,
      isInvoiceable: {
        type: "textFilter",
        value: isShowInvoiceableApplied ? "" : "true",
      },
    });
  };

  return (
    <main className={styles.transactionsList}>
      <div className={styles.transactionsListHeaderBlock}>
        <h1 className={styles.transactionsListTitle}>
          {t("transactions.grid.title")}
          {!!rowCount && (
            <span className={styles.transactionsListTotalCount}>
              {rowCount}
            </span>
          )}
        </h1>
        <div className={styles.transactionsListButtons}>
          {canExportTransactionsList && (
            <ExportExcelData
              dataRows={[]}
              columnConfig={columnConfig}
              getDataRows={getDataRows}
              buttonId="export-transactions-button"
              fileName={t("transactions.grid.export.fileName")}
              buttonLabel={t("transactions.grid.export.buttonLabel")}
            />
          )}
          {isPictusJsonExportable && (
            <ExportPictusJson
              getExportParams={getExportParams}
              userEmail={user?.email || ""}
              onError={onExportError}
              buttonId="export-pictus-json-button"
              buttonLabel={t("transactions.grid.export.pictusJsonButtonLabel")}
            />
          )}
          <CreateInvoiceButton
            canCreateInvoice={canCreateInvoice}
            businessRoleCode={businessRoleCode}
            displayName={displayName}
            isWithTransactions
            listQueryKey={transactionsQueryKey}
            getItemNumbers={getTransactionNumbers}
            setIsShowInvoiceable={setIsShowInvoiceable}
            isShowInvoiceableApplied={isShowInvoiceableApplied}
            isDisabled={!isCreateInvoiceEnabled}
          />
        </div>
      </div>
      <DataGrid
        idField="transactionNumber"
        rowSelection="multiple"
        suppressCellSelection
        assignGridApi={assignGridApi}
        onFirstDataRendered={onFirstDataRendered}
        columnDefs={columnDefs}
        rowData={rowData}
        isLoading={transactions.isLoading}
        fetchMore={transactions.fetchMore}
        canFetchMore={transactions.canFetchMore}
        isFetchingMore={transactions.isFetchingMore}
        getRowHeight={getTransactionRowHeight}
        onSelectionChanged={(e) => {
          const rows = e.api.getSelectedRows();

          setSelectedRows(rows);
          setIsAllSelected(!!rows.length && rows.length === rowData?.length);
        }}
        className={styles.transactionsListDataGrid}
        frameworkComponents={renderers}
        filters={filters}
        {...otherProps}
      />
    </main>
  );
};

export default TransactionsListGrid;
